import { Component, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { UserInfo } from '../../auth/store/models/user-info.interface';
import * as AuthSelectors from '../../auth/store/selectors/auth.selector';
import { ModalService, ErrorMessagesConfig } from '@frogo-kit/core';
import { ActionsSubject, Store } from '@ngrx/store';
import { OtpInitialization } from '../../interfaces/otp-initialization.interface';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as AuthActions from '../../auth/store/actions/auth.actions';
import { ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';

@UntilDestroy()
@Component({
    selector: 'aft-otp-setup',
    templateUrl: './otp-setup.component.html',
    styleUrl: './otp-setup.component.scss',
    standalone: false,
})
export class OtpSetupComponent implements OnInit {
  public title: string = '';
  public otpData: OtpInitialization;
  public otpCodeControl = new FormControl('', [
    Validators.required,
    Validators.minLength(6),
    Validators.maxLength(6),
  ]);
  public userInfo: UserInfo;
  public errorsConfig: ErrorMessagesConfig = {
    otpCodeInvalid: () => this._translate.instant('loginOtpError'),
  };
  public userHasOtp: boolean = false;

  private _userData$ = this._store.select(AuthSelectors.userDataSelector);
  private _has2FAInit$ = this._store.select(AuthSelectors.has2FAInit);

  constructor(
    private readonly _modal: ModalService,
    private readonly _store: Store,
    private _domSanitizer: DomSanitizer,
    private _actionsStream: ActionsSubject,
    private _translate: TranslateService,
  ) {}

  public ngOnInit(): void {
    this._initSubs();
    this.setUserHasOtp();
  }

  public onCloseModal() {
    this._modal.hide();
  }

  public setup2fA() {
    if (this.userHasOtp) {
      this._store.dispatch(AuthActions.disable2FA({ code: this.otpCodeControl.value }));
    } else {
      if (this.otpCodeControl.valid) {
        this._store.dispatch(AuthActions.verify2FACode({ code: this.otpCodeControl.value }));
      }
    }
  }

  protected getImageSrc(): SafeResourceUrl {
    return this._domSanitizer.bypassSecurityTrustResourceUrl(
      `data:image/png;base64,${this.otpData?.qr}`,
    );
  }

  private _initSubs(): void {
    this._has2FAInit$.pipe(untilDestroyed(this)).subscribe(otpInitData => {
      this.otpData = otpInitData;
    });

    this._actionsStream
      .pipe(
        untilDestroyed(this),
        ofType(
          AuthActions.verify2FACodeFailure,
        ),
      )
      .subscribe(() => {
        this.otpCodeControl.setErrors({
          otpCodeInvalid: true,
        });
      });

    this._actionsStream
      .pipe(
        untilDestroyed(this),
        ofType(
          AuthActions.verify2FACodeSuccess,
        ),
      )
      .subscribe(() => {
        this._modal.hide();
      });

    this._actionsStream
      .pipe(
        untilDestroyed(this),
        ofType(
          AuthActions.disable2FASuccess,
        ),
      )
      .subscribe(() => {
        this._modal.hide();
      });
  }

  private setUserHasOtp(): void {
    this._userData$.pipe(untilDestroyed(this), take(1))
      .subscribe(({ userData }) => {
      this.userHasOtp = userData.enabledOtp;
      if (!this.userHasOtp) {
        this._store.dispatch(AuthActions.getOtpInitData());
      }
    })
  }
}
